import React, { useCallback } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface EMSineWaveProps {
  width?: number;
  height?: number;
}

export const EMSineWave: React.FC<EMSineWaveProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerY = size / 2;
    const speed = frameCount * 0.05;
    
    // Draw central axis
    ctx.beginPath();
    ctx.moveTo(20, centerY);
    ctx.lineTo(size - 20, centerY);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.lineWidth = 1;
    ctx.stroke();

    const amplitude = 60;
    const frequency = 0.05;

    // Draw Magnetic Field (Pink, Z-axis projection)
    ctx.beginPath();
    ctx.strokeStyle = '#F472B6'; // Pink
    ctx.lineWidth = 2.5;
    for (let x = 20; x < size - 20; x++) {
      // Oscillate based on x and time
      const z = Math.sin((x * frequency) - speed) * amplitude;
      
      // Project Z to 2D (diagonal)
      const projX = x - z * 0.4;
      const projY = centerY + z * 0.4;
      
      if (x === 20) ctx.moveTo(projX, projY);
      else ctx.lineTo(projX, projY);
    }
    ctx.stroke();

    // Draw Electric Field (Blue, Y-axis)
    ctx.beginPath();
    ctx.strokeStyle = '#60A5FA'; // Blue
    ctx.lineWidth = 2.5;
    for (let x = 20; x < size - 20; x++) {
      const y = Math.sin((x * frequency) - speed) * amplitude;
      if (x === 20) ctx.moveTo(x, centerY - y);
      else ctx.lineTo(x, centerY - y);
    }
    ctx.stroke();

    // Labels
    ctx.font = '14px sans-serif';
    ctx.fillStyle = '#E2E8F0';
    ctx.textAlign = 'left';
    ctx.fillText('No medium needed →', 20, 30);

    // Dynamic labels that follow a peak
    const peakX = ((Math.PI / 2 + speed) / frequency) % (size - 80) + 40;
    const peakY_E = centerY - amplitude;
    const peakZ = amplitude;
    const peakY_B = centerY + peakZ * 0.4;
    const peakX_B = peakX - peakZ * 0.4;

    // E-field label
    ctx.fillStyle = '#60A5FA';
    ctx.fillText('Electric Field', peakX - 20, peakY_E - 15);
    
    // B-field label
    ctx.fillStyle = '#F472B6';
    ctx.fillText('Magnetic Field', peakX_B - 20, peakY_B + 25);

  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};
